/* eslint-disable jsx-a11y/accessible-emoji */

import {
    Box,
    Button,
    Flex,
    Menu,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spacer,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure,
    useToast
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdDelete, MdFileUpload } from "react-icons/md";
import { Link, useParams } from "react-router-dom";
import { AccountAvatar } from "../../components/AccountAvatar";
import { Loader } from "../../components/Loader";
import { Title } from "../../components/Title";
import { DocumentForm } from "../../forms";
import {
    GetMedicalCardDocument,
    useAddDocumentToMedicalCardMutation,
    useDeleteDocumentFromMedicalCardMutation,
    useGetMedicalCardQuery,
    useMeQuery
} from "../../graphql";
import { useDashboardTitle } from "../../hooks/useDashboardTitle";
import { formatDateTime, getFullName, truncate } from "../../utils";

export const MedicalCardDocuments: React.FC = () => {
    const { t } = useTranslation();
    const { setTitle } = useDashboardTitle();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { visitId } = useParams<{ visitId: string }>();
    const toast = useToast();

    const { data: observer, loading: loadingMe } = useMeQuery({
        fetchPolicy: "cache-first"
    });

    const [addDocumentToMedicalCard] = useAddDocumentToMedicalCardMutation();
    const { data, loading } = useGetMedicalCardQuery({
        variables: {
            id: parseInt(visitId)
        }
    });
    const [
        deleteDocumentFromMedicalCard
    ] = useDeleteDocumentFromMedicalCardMutation();

    const medicalCard = data?.getMedicalCard;

    const onDocumentCreate = async (values) => {
        await addDocumentToMedicalCard({
            variables: {
                medicalCardId: medicalCard.id,
                description: values.description,
                key: values.media.key
            },
            refetchQueries: [
                {
                    query: GetMedicalCardDocument,
                    variables: {
                        id: parseInt(visitId)
                    }
                }
            ]
        });

        onClose();

        toast({
            title: "Sukses",
            description: t("patients.medicalCards.documentsTab.documentAdded"),
            status: "success",
            isClosable: true
        });
    };

    const onDocumentDelete = async (id) => {
        await deleteDocumentFromMedicalCard({
            variables: {
                id
            },
            refetchQueries: [
                {
                    query: GetMedicalCardDocument,
                    variables: {
                        id: parseInt(visitId)
                    }
                }
            ]
        });

        onClose();

        toast({
            title: "Sukses",
            description: t("patients.medicalCards.documentsTab.documentDeleted"),
            status: "success",
            isClosable: true
        });
    };

    useEffect(() => {
        if (!loading) {
            setTitle([
                t("patients.medicalCards.detailsTab.accounts"),
                {
                    to: `/account/${medicalCard?.patient?.id}`,
                    text: getFullName(medicalCard?.patient)
                },
                t("patients.medicalCards.detailsTab.visitOnDate", {
                    date: formatDateTime(medicalCard.createdAt)
                })
            ]);
        }

    // eslint-disable-next-line
  }, [loading]);

    return (
        <Box
            borderWidth="1px"
            borderRadius="lg"
            overflow="hidden"
            p={ 2 }
            background="white"
        >
            <Modal closeOnOverlayClick={ false } isOpen={ isOpen } onClose={ onClose }>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        { t("patients.medicalCards.documentsTab.add") }
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={ 6 }>
                        <DocumentForm onSubmit={ onDocumentCreate } />
                    </ModalBody>
                </ModalContent>
            </Modal>
            { loading || loadingMe ? (
                <Loader />
            ) : (
                <>
                    <Title title={ t("patients.medicalCards.documentsTab.list") }>
                        <Button
                            leftIcon={ <MdFileUpload /> }
                            colorScheme="blue"
                            size="sm"
                            variant="ghost"
                            onClick={ onOpen }
                        >
                            { t("patients.medicalCards.documentsTab.upload") }
                        </Button>
                    </Title>
                    <Table variant="striped">
                        <Thead>
                            <Tr>
                                <Th width="250px">
                                    { t("patients.medicalCards.documentsTab.author") }
                                </Th>
                                <Th>{ t("patients.medicalCards.documentsTab.description") }</Th>
                                <Th isNumeric></Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            { medicalCard.documents.map((document) => {
                                return (
                                    <Tr key={ document.id }>
                                        <Td>
                                            <Link
                                                to={ `/account/${medicalCard.patient.id}/visit/${medicalCard.id}/document/${document.id}` }
                                            >
                                                <AccountAvatar
                                                    account={ document.author }
                                                    size="sm"
                                                    role={ false }
                                                />
                                            </Link>
                                        </Td>

                                        <Td>
                                            <Link
                                                to={ `/account/${medicalCard.patient.id}/visit/${medicalCard.id}/document/${document.id}` }
                                            >
                                                { truncate(document.description) }{ " " }
                                            </Link>
                                        </Td>
                                        <Td isNumeric>
                                            <Button
                                                colorScheme="red"
                                                variant="solid"
                                                disabled={ document.author.id !== observer.me.id }
                                                onClick={ () => onDocumentDelete(document.id) }
                                            >
                                                <MdDelete />
                                            </Button>
                                        </Td>
                                    </Tr>
                                );
                            }) }
                        </Tbody>
                    </Table>
                </>
            ) }
        </Box>
    );
};
